// src/_services/formEngine.ts
// Dynamic form types + scoring engine (ported & improved from v1)

// ─── Types ────────────────────────────────────────────────────
export type FieldType = 'text' | 'number' | 'date' | 'radio' | 'checkbox' | 'textarea' | 'select' | 'url' | 'phone'

export type Option = { label: string; value: string }

export type Field = {
  id: string
  name: string
  type: FieldType
  label: string
  required: boolean
  multiple?: boolean        // สำหรับ checkbox / select multi
  options?: Option[]
  placeholder?: string
  scorable?: boolean        // ฟิลด์นี้ใช้คิดคะแนนได้
}

export type Page = {
  id: string
  title: string
  fields: Field[]
}

export type FormDoc = {
  id: string
  title: string
  pages: Page[]
  updatedAt?: number
}

// ─── Scoring Types ────────────────────────────────────────────
export type Threshold   = { op: '<='; value: number; score: number }
export type FormulaRule = { id: string; label?: string; formula: string; thresholds: Threshold[] }
export type TimeRule    = { dateVar: string; thresholds: Threshold[] }
export type StringRule  = { varName: string; mapping: { value: string; score: number }[] }
export type NumbRule    = { varName: string; thresholds: Threshold[] }

export type PointDoc = {
  formulaRules?: FormulaRule[]
  timeRules?:    TimeRule[]
  stringRules?:  StringRule[]
  numbRule?:     NumbRule[]
}

export type FormulaResult = { label?: string; value: number | null; score: number | null }
export type ScoreLine     = { var: string; score: number | null }

// ─── Scoring Engine ───────────────────────────────────────────
function yearsBetween(fromISO: string, toISO: string): number {
  const from = new Date(fromISO)
  const to   = new Date(toISO)
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return NaN
  return (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24 * 365.25)
}

export function applyThresholds(val: number, ths: Threshold[]): number | null {
  const sorted = [...ths].sort((a, b) => b.value - a.value)
  for (const t of sorted) if (val <= t.value) return t.score
  return null
}

export function evaluateFormula(expr: string, ctx: Record<string, any>): number {
  const today = new Date().toISOString().slice(0, 10)
  let s = (expr || '').replace(/now\s*-\s*([a-zA-Z_][a-zA-Z0-9_]*)/g, (_, v: string) => {
    const yrs = yearsBetween(ctx[v] || '', today)
    return Number.isFinite(yrs) ? String(yrs) : 'NaN'
  })
  s = s.replace(/[a-zA-Z_][a-zA-Z0-9_]*/g, (name) => {
    if (name === 'now') return '0'
    const v = ctx[name]
    if (v == null || v === '') return 'NaN'
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? String(n) : 'NaN'
  })
  if (!/^[0-9+\-*/().\sNa]+$/.test(s)) throw new Error('สูตรไม่ถูกต้อง')
  // eslint-disable-next-line no-new-func
  const r = Function(`'use strict'\nreturn (${s})`)()
  if (typeof r !== 'number' || !Number.isFinite(r)) throw new Error('ผลลัพธ์ไม่เป็นตัวเลข')
  return r
}

export function calcAllScores(point: PointDoc, formData: Record<string, any>) {
  const today = new Date().toISOString().slice(0, 10)

  const formulaResults: FormulaResult[] = (point.formulaRules || []).map((r) => {
    try {
      const val = evaluateFormula(r.formula || '', formData)
      return { label: r.label, value: val, score: applyThresholds(val, r.thresholds || []) }
    } catch { return { label: r.label, value: null, score: null } }
  })

  const timeScores: ScoreLine[] = (point.timeRules || []).map((r) => {
    if (!r.dateVar) return { var: '', score: null }
    const yrs = r.dateVar ? yearsBetween(formData[r.dateVar] || '', today) : NaN
    return { var: r.dateVar, score: Number.isFinite(yrs) ? applyThresholds(yrs, r.thresholds || []) : null }
  })

  const stringScores: ScoreLine[] = (point.stringRules || []).map((r) => {
    if (!r.varName) return { var: '', score: null }
    const m = (r.mapping || []).find((x) => x.value === String(formData[r.varName]))
    return { var: r.varName, score: m ? m.score : null }
  })

  const numbScores: ScoreLine[] = (point.numbRule || []).map((r) => {
    if (!r.varName) return { var: '', score: null }
    const n = Number(formData[r.varName])
    return { var: r.varName, score: Number.isFinite(n) ? applyThresholds(n, r.thresholds || []) : null }
  })

  const sum = (arr: { score: number | null }[]) => arr.reduce((s, x) => s + (x.score ?? 0), 0)
  const totalPoint = sum(formulaResults) + sum(timeScores) + sum(stringScores) + sum(numbScores)

  return { formulaResults, timeScores, stringScores, numbScores, totalPoint }
}

// ─── Form helpers ─────────────────────────────────────────────
export function buildInitialValues(fd: FormDoc | null): Record<string, any> {
  const init: Record<string, any> = {}
  for (const pg of fd?.pages || []) {
    for (const f of pg.fields || []) {
      if (f.type === 'checkbox') init[f.name] = f.multiple ? [] : false
      else if (f.type === 'select' && f.multiple) init[f.name] = []
      else init[f.name] = ''
    }
  }
  return init
}

export function validatePage(form: FormDoc | null, pageIndex: number, formData: Record<string, any>): string[] {
  const pg   = form?.pages?.[pageIndex]
  const errs: string[] = []
  if (!pg) return errs
  for (const f of pg.fields || []) {
    if (!f.required) continue
    const v = formData[f.name]
    if (f.type === 'checkbox' && f.multiple) {
      if (!Array.isArray(v) || v.length === 0) errs.push(`กรุณาเลือกอย่างน้อย 1 รายการ: ${f.label}`)
    } else if (f.type === 'select' && f.multiple) {
      if (!Array.isArray(v) || v.length === 0) errs.push(`กรุณาเลือก: ${f.label}`)
    } else {
      if (v === '' || v == null) errs.push(`กรุณากรอก: ${f.label}`)
    }
  }
  return errs
}
